import {
  CUTOFF_PRESETS,
  ONCE_OPENS_OPTIONS,
  OPENS_OPTIONS,
  reminderBody,
  stepValid,
  WINDOW_PRESETS
} from '@/config/dropWizardSteps'

const toId = value => (value === null || value === undefined || value === '' ? null : Number(value))

const arrivalPayload = arrival => {
  const preset = WINDOW_PRESETS.find(option => option.value === arrival.window)
  const custom = !preset || preset.value === 'custom'
  return {
    offset_days: arrival.offsetDays,
    start: custom ? arrival.start : preset.start,
    end: custom ? arrival.end : preset.end
  }
}

const closesPayload = ordering => {
  const preset = CUTOFF_PRESETS.find(option => option.value === ordering.cutoff)
  const custom = !preset || preset.value === 'custom'
  return {
    days_before: custom ? ordering.closesDaysBefore : preset.daysBefore,
    time: custom ? ordering.closesTime : preset.time
  }
}

const opensPayload = ordering => {
  if (ordering.opens === 'previous') return { kind: 'previous', days: null }
  if (ordering.opens === 'now') return { kind: 'now', days: null }
  const option = [...OPENS_OPTIONS, ...ONCE_OPENS_OPTIONS].find(item => item.value === ordering.opens)
  return { kind: 'days_before', days: option?.days ?? null }
}

const newMethodPayload = method => ({
  type: method.type,
  name: method.name.trim(),
  address: method.type === 'pickup' ? { ...method.address } : null,
  zone: method.type === 'delivery' ? zonePayload(method.zone) : null
})

const zonePayload = zone => ({
  kind: zone.kind,
  zip_codes: zone.kind === 'zips' ? zone.zipCodes : [],
  states: zone.kind === 'states' ? zone.states : [],
  waypoints: zone.kind === 'map' ? zone.waypoints.map(([lat, lng]) => ({ lat, lng })) : []
})

const fulfillmentPayload = fulfillment =>
  fulfillment.newMethod
    ? { fulfillment_method_id: null, new_method: newMethodPayload(fulfillment.newMethod) }
    : { fulfillment_method_id: toId(fulfillment.methodId), new_method: null }

const audiencePayload = audience => {
  if (audience.kind === 'tiers') return { kind: 'tiers', audience_ids: audience.tierAudienceIds.map(Number) }
  if (audience.kind === 'lists') return { kind: 'lists', audience_ids: audience.lists.map(list => Number(list.id)) }
  return { kind: 'everyone', audience_ids: [] }
}

const reminderPayload = (reminder, data) => ({
  when: reminder.when,
  days_before: reminder.when === 'custom' ? reminder.daysBefore : null,
  time: reminder.when === 'custom' ? reminder.time : null,
  body: reminderBody(reminder, data),
  attach_vcard: reminder.attachVcard
})

const paymentPayload = payment => {
  const kind = payment.autofill ? payment.autofillKind : 'none'
  return {
    autocharge: payment.autocharge,
    autofill: kind,
    autofill_tip: payment.autofill && kind !== 'cart' ? payment.autofillTip : false,
    cart_items:
      kind === 'cart'
        ? payment.cartItems.map(item => ({ product_id: Number(item.productId), quantity: item.quantity }))
        : []
  }
}

export const buildSetupPayload = data => ({
  name: data.name.trim(),
  store_id: toId(data.storeId),
  rhythm: data.rhythm,
  weekdays: data.rhythm === 'once' ? [] : [...data.weekdays].sort((a, b) => a - b),
  date: data.rhythm === 'once' ? data.date : null,
  fulfillment: fulfillmentPayload(data.fulfillment),
  arrival: arrivalPayload(data.arrival),
  ordering: {
    closes: closesPayload(data.ordering),
    opens: opensPayload(data.ordering),
    limit: data.ordering.limitOn ? data.ordering.limit : null
  },
  audience: audiencePayload(data.audience),
  reminders: data.reminders.map(reminder => reminderPayload(reminder, data)),
  payment: paymentPayload(data.payment)
})

// The preview runs the server's full validation, so steps the merchant has not
// reached yet are stood in for: everyone, no autocharge, only written reminders,
// and the store's first method until one is chosen.
export const buildPreviewPayload = (data, { fallbackMethodId }) => {
  if (!['what', 'often', 'day'].every(key => stepValid(key, data))) return null
  const methodId = data.fulfillment.methodId || (data.fulfillment.newMethod ? null : fallbackMethodId)
  if (!methodId && !data.fulfillment.newMethod) return null
  const payload = buildSetupPayload({
    ...data,
    fulfillment: data.fulfillment.newMethod ? data.fulfillment : { methodId, newMethod: null }
  })
  if (!data.audience.kind || (data.audience.kind !== 'everyone' && !payload.audience.audience_ids.length))
    payload.audience = { kind: 'everyone', audience_ids: [] }
  if (typeof data.payment.autocharge !== 'boolean') payload.payment = { ...payload.payment, autocharge: false }
  payload.reminders = payload.reminders.filter(reminder => reminder.body.trim().length > 0)
  return payload
}
